// Copyright (c) StrangeBee 2023

import client from '@Client';
import { handleNotificationError, useLocalMessage } from '@Helpers/';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';
import { TaskLogManage } from '@Organisms/TaskLog/TaskLogManage/TaskLogManage';
import React, { ReactElement, useCallback } from 'react';
import { TaskLogInput, TaskLogOutput, TaskOutput, TaskStatus } from 'thehive-sdk';
import { TaskEventList } from '../eventList';
import { ICONS } from '@Constants/iconsApp';

import './RequireActionButton.scss';

interface RequireActionButtonProps {
    eventList: TaskEventList;
    taskId: string;
    organisationName: string;
    actionRequired: boolean;
    label: {
        requireAction: string;
        cancelAction: string;
    };
    task?: TaskOutput;
    needLog?: boolean;
}
export const RequireActionButton = ({
    eventList,
    taskId,
    organisationName,
    actionRequired,
    label,
    task,
    needLog = false,
}: RequireActionButtonProps): ReactElement => {
    const i = useLocalMessage();

    const handleActionRequired = useCallback(
        (actionRequired: boolean, organisationName: string, taskLog?: TaskLogInput) => {
            if (taskLog) {
                client.TaskLogController.create<TaskLogInput, TaskLogOutput>(taskLog, taskId).catch(
                    handleNotificationError,
                );
            }
            if (!actionRequired) {
                return eventList.markActionAsRequired(organisationName, taskId);
            } else {
                return eventList.markActionAsDone(organisationName, taskId);
            }
        },
        [taskId, eventList],
    );

    return (
        <THPopConfirm
            title={actionRequired ? i('task.actionRequired.done') : i('task.actionRequired.require-caption')}
            okText={
                <TaskLogManage
                    task={task}
                    onTaskClose={() => eventList.onEdit({ status: TaskStatus.COMPLETED }, taskId)}
                    onFormFinish={(taskLog): Promise<any> => {
                        return handleActionRequired(actionRequired, organisationName, taskLog);
                    }}
                    triggerElem={<THButton size="small">{i('task.actionRequired.log.with')}</THButton>}
                />
            }
            okType="link"
            cancelText={i('task.actionRequired.log.without')}
            onCancel={(): void => {
                handleActionRequired(actionRequired, organisationName);
            }}
            hiddenCancel={actionRequired && needLog}
            placement="top"
        >
            <THButton
                type="link"
                danger={actionRequired}
                icon={actionRequired && <ICONS.WarningOutlined />}
                className="require-action-button"
            >
                {actionRequired ? label.cancelAction : label.requireAction}
            </THButton>
        </THPopConfirm>
    );
};
